import { containers, inputs } from '@ecosystem/newron-design-system';
import { InitialSelectedListItem } from '@ecosystem/newron-design-system/build/components/listBox/types';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useAnalyticsContext } from '../../analytics/analytics.provider';
import { ContextManager } from '../../context/manager';
import { useDataState } from '../../data/provider';
import { CustomTimeSeriesModal } from '../../elements/timeSeries/custom/timeSeries.custom.modal';
import constants from '../../filters/constants';
import { useSaveFeatureSegments } from '../../filters/saveFeatureSegments.hook';
import { useSavePriceClasses } from '../../filters/savePriceClasses.hook';
import { formatFeatureClassForParams, FormatFeatureClassInput, formatPricesForParams } from '../../filters/utils';
import { useCorePageState } from '../../page/provider';
import {
  Context,
  DataColumn,
  EventName,
  FilterSections,
  GroupedListBoxMenuItem,
  ListBoxMenuItem,
  ListMenuItem,
  MenuItemDataColumns,
  MenuItemOption,
  Params,
  Period,
  SelectionMenu,
  TrackingElementType,
} from '../../types';
import { MenuContextButton } from './menu.context.button';
import { CustomComparisonModal } from './menu.custom.modal';
import { MenuEditButton } from './menu.edit.button';
import {
  MenuButtonProps,
  MenuColumnOption,
  SecondFeatureSegmentationConfig,
  SelectedCustomFeatureContext,
} from './types';
import {
  checkIfCustomPriceTier,
  checkIsApplicableSavedFeature,
  flattenMenuItems,
  generateCustomLabel,
  getInitialFeatureClass,
  handleSelectColumnItem,
  handleViewAllClick,
  updateWithCustomClass,
} from './utils';
import { appendBrandOwnerLabel } from '../../page/utils';
import { BRAND_OWNER_DELIMITER, BRAND_OWNER_GROUP_PREFIX } from '../brandOwner.constants';
import classNames from 'classnames';
import { useMobile } from '../../context/mobile/mobile.provider';
import { UpsellFooter } from './menu.upsell.footer';
import { formatSharedBrandsTotal } from '../../elements/utils';
import { SavedCustomFeatureSegments } from '../../types/content/custom.types';
import { SEGMENT_CUSTOM_FEATURE, FEATURE_SEGMENT } from './constants';

const { FilterTrayTabId } = containers;
const { ListBox } = inputs;

type Props = {
  menuConfig: SelectionMenu;
  values: Params;
  onUpdate: (updates: Params) => void;
  button: FunctionComponent<MenuButtonProps>;
  defaultValue?: string;
  inactive?: boolean;
  buttonLabel?: string;
  showGroupLabel?: boolean;
  isSubMenuIndented?: boolean;
  useListBoxButtonSlot?: boolean;
};
function SelectionMenuComponent({
  menuConfig,
  values,
  onUpdate,
  button,
  defaultValue,
  buttonLabel,
  inactive,
  showGroupLabel,
  isSubMenuIndented,
  useListBoxButtonSlot,
}: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<ListMenuItem[]>([]);
  const [selectedMenuOption, setSelectedMenuOption] = useState<MenuItemOption>(null);
  const [selectedPath, setSelectedPath] = useState<InitialSelectedListItem>(null);
  const [dataColumns, setDataColumns] = useState<MenuColumnOption[]>([]);
  const [selectedCustomFeature, setSelectedCustomFeature] = useState<SelectedCustomFeatureContext>();
  const [shouldDisableCustom, setShouldDisableCustom] = useState<boolean>(false);
  const [menuCustomItem, setMenuCustomItem] = useState<ListBoxMenuItem>(null);
  const [customPeriodLabel, setCustomPeriodLabel] = useState<string | undefined>(null);
  const [customTimeSeriesLabel, setCustomTimeSeriesLabel] = useState<string | null>(null);
  const { data, isDataLoading: dataLoading } = useDataState();
  const {
    dslLoading,
    contextLoading,
    setCustomizationTray,
    trayType,
    setCustomizationTrayTabId,
    setIsFilterTray,
    page,
  } = useCorePageState();
  const isLoading = dslLoading || contextLoading || dataLoading;
  const context = ContextManager.getInstance().get();
  const pageCustomFeatures = context?.customFeature?.[page.name];

  const { trackPageEvent } = useAnalyticsContext();

  const { savedPriceClasses, setIsActive } = useSavePriceClasses();

  const { applicableSavedFeatureSegments } = useSaveFeatureSegments();
  const { PRICE_CUSTOMIZATION, FEATURE_CUSTOMIZATION } = constants;
  const [sections, setSections] = useState<FilterSections[]>();

  const {
    items,
    paramName,
    preventDuplicatesWith = [],
    customizationTraySettings,
    customMenuModalSettings,
  } = menuConfig;

  useEffect(() => {
    const subscriber = (context: Context) => {
      if (context) {
        const {
          filters: { sections },
        } = context;
        setSections(sections);

        const contextPageCustomFeatures = context?.customFeature?.[page.name];
        const menuContext = contextPageCustomFeatures?.[paramName];

        // Detect orphaned custom feature (URL has custom feature but context doesn't)
        const currentValue = values[paramName]?.[0];
        const hasCustomFeatureInURL = currentValue?.includes(`${SEGMENT_CUSTOM_FEATURE}|${FEATURE_SEGMENT}`);

        const shouldResetToDefault = page.name === 'segments' && hasCustomFeatureInURL && !menuContext?.selectedFeature;

        // If URL has custom feature but context is missing it, reset to default
        if (shouldResetToDefault) {
          handleOrphanedCustomFeatureReset();
          return;
        }

        if (menuContext?.selectedFeature) {
          setSelectedCustomFeature(menuContext.selectedFeature);
        } else {
          setSelectedCustomFeature(undefined);
        }
      }
    };

    ContextManager.getInstance().subscribeToSettingsChanges(subscriber);
    return () => {
      ContextManager.getInstance().unsubscribeFromSettingsChanges(subscriber);
    };
  }, [paramName, page?.name]);

  useEffect(() => {
    setCustomPeriodLabel(undefined);
  }, [dslLoading, contextLoading]);

  const baseFeatures = sections?.find(item => item?.label === 'Features')?.ungrouped?.definitions;

  useEffect(() => {
    const subscriber = (context: Context) => {
      if (context) {
        const contextManager = ContextManager.getInstance();
        setShouldDisableCustom(contextManager.shouldDisableCustomOptions(paramName));
      }
    };
    ContextManager.getInstance().subscribeToSettingsChanges(subscriber);
    return () => ContextManager.getInstance().unsubscribeFromSettingsChanges(subscriber);
  }, [paramName]);

  const handleOrphanedCustomFeatureReset = () => {
    const newValues: Params = { ...values };

    cleanupCustomFeatureTiers(newValues);

    const flatItems = flattenMenuItems(items);
    const firstValidItem = flatItems.find(
      item =>
        item.type === 'MenuItem' &&
        !item.isCustom &&
        !item.isDisabled &&
        !item.isBrand &&
        typeof item.value === 'string'
    ) as ListBoxMenuItem;

    let resetValue: string[];
    if (firstValidItem?.value) {
      resetValue = [firstValidItem.value as string];
    } else if (defaultValue) {
      resetValue = [defaultValue];
    } else {
      resetValue = [];
    }
    newValues[paramName] = resetValue;

    onUpdate(newValues);
    setSelectedCustomFeature(undefined);
  };

  const cleanupCustomFeatureTiers = (newValues: Params) => {
    if (!newValues.customFeatureTiers) {
      return;
    }

    newValues.customFeatureTiers = newValues.customFeatureTiers.filter(tier => !tier.startsWith(`${paramName}:`));

    if (newValues.customFeatureTiers.length === 0) {
      delete newValues.customFeatureTiers;
    }
  };

  const valuesToPrevent = useMemo(() => {
    const prevented: string[] = [];

    preventDuplicatesWith.forEach(key => {
      const menuValues = values[key] || [];
      menuValues.forEach(value => {
        prevented.push(value);

        if (value && !value.includes('segment_') && !value.includes('|')) {
          prevented.push(`${value}_id`);
        }
      });
    });

    if (!pageCustomFeatures) {
      return prevented;
    }

    Object.keys(pageCustomFeatures).forEach(menuKey => {
      if (menuKey === paramName || !preventDuplicatesWith.includes(menuKey)) {
        return;
      }

      const otherMenuContext = pageCustomFeatures[menuKey];
      const selectedFeature = otherMenuContext?.selectedFeature;

      if (selectedFeature?.value) {
        prevented.push(selectedFeature.value);
        const baseFeature = selectedFeature.value.split('_id')[0];
        prevented.push(baseFeature);
      }
    });

    return prevented;
  }, [values, preventDuplicatesWith, pageCustomFeatures, paramName]);

  const isInvalidMenuKey = (key: string, currentParam: string): boolean => {
    return !key || key === 'undefined' || key === currentParam;
  };

  const shouldDisableStandardFeature = (standardFeatureValue: string): boolean => {
    if (!preventDuplicatesWith?.length || !pageCustomFeatures) {
      return false;
    }

    for (const menuKey of preventDuplicatesWith) {
      if (menuKey === paramName || menuKey === 'undefined' || !menuKey) {
        continue;
      }

      const otherMenuValues = values[menuKey];
      if (!otherMenuValues?.length) {
        continue;
      }

      const otherMenuValue = otherMenuValues[0];
      const isOtherMenuUsingCustomFeature = otherMenuValue?.includes(SEGMENT_CUSTOM_FEATURE);

      if (!isOtherMenuUsingCustomFeature) {
        continue;
      }

      const menuContext = pageCustomFeatures[menuKey];
      if (!menuContext?.selectedFeature?.value) {
        continue;
      }

      const customFeatureBase = menuContext.selectedFeature.value;
      const customFeaturePrefix = customFeatureBase.split('_id')[0];

      if (standardFeatureValue === customFeaturePrefix) {
        return true;
      }
    }

    return false;
  };

  const shouldDisableCustomFeature = useCallback(
    (savedFeature: SavedCustomFeatureSegments) => {
      const savedFeatureBase = savedFeature.segmentationName.split('_id')[0];

      for (const menuKey of preventDuplicatesWith) {
        if (isInvalidMenuKey(menuKey, paramName)) {
          continue;
        }

        const otherMenuValues = values[menuKey];
        if (!otherMenuValues?.length) {
          continue;
        }

        const otherMenuValue = otherMenuValues[0];
        if (!otherMenuValue?.includes(SEGMENT_CUSTOM_FEATURE) && otherMenuValue === savedFeatureBase) {
          return true;
        }
      }

      if (valuesToPrevent.includes(savedFeature.segmentationName)) {
        return true;
      }

      if (isInvalidMenuKey(savedFeature.name, paramName)) {
        return true;
      }

      if (!pageCustomFeatures) {
        return false;
      }

      return Object.keys(pageCustomFeatures).some(menuKey => {
        if (isInvalidMenuKey(menuKey, paramName)) {
          return false;
        }

        const menuContext = pageCustomFeatures[menuKey];
        if (!menuContext?.selectedFeature) {
          return false;
        }

        const otherMenuValues = values[menuKey];
        const isOtherMenuCustomFeature =
          otherMenuValues?.length > 0 && otherMenuValues?.[0]?.includes(SEGMENT_CUSTOM_FEATURE);

        if (!isOtherMenuCustomFeature) {
          return false;
        }

        const selectedFeature = menuContext.selectedFeature;
        return (
          selectedFeature.savedName === savedFeature.name || selectedFeature.value === savedFeature.segmentationName
        );
      });
    },
    [valuesToPrevent, pageCustomFeatures, paramName, values, preventDuplicatesWith]
  );

  useEffect(() => {
    const menuItems: ListMenuItem[] = [];
    items.forEach(item => {
      if (item?.hasFooter) {
        item.footerContent = <UpsellFooter footerType="feature" displayUpsellFooter={true} />;
      }
      if (item.type === 'DataColumns') {
        const allColumns: DataColumn[] = [...(data?.getDataColumns() || []), ...(data?.getAvailableColumns() || [])];
        const { ofType, requireArrayLength } = item as MenuItemDataColumns;
        const columnOptions: MenuColumnOption[] = allColumns
          .filter(col => {
            let isMatch = ofType.includes(col.dataType);
            if (isMatch && requireArrayLength) {
              isMatch = col.arrayLength !== null;
            }
            return isMatch;
          })
          .map(col => ({
            value: col.name,
            label: col.label,
            enrichment: col.enrichment,
          }));
        setDataColumns(columnOptions);

        columnOptions.forEach(opt => {
          const menuItem: ListBoxMenuItem = {
            type: 'MenuItem',
            value: opt.value,
            label: opt.label,
          };
          menuItems.push(menuItem);
        });
      } else {
        updateDefaultCustomTimeSeriesLabel(item);

        const hasConflictDetection = preventDuplicatesWith.length > 0 && pageCustomFeatures;
        const needsCustomDisabling = shouldDisableCustom || hasConflictDetection;

        const processedItem = needsCustomDisabling ? applyCustomOptionDisabling(item, shouldDisableCustom) : item;
        menuItems.push(processedItem);
      }
    });
    setMenuItems(menuItems);
  }, [data, items, shouldDisableCustom]);

  const applyCustomOptionDisabling = (item: ListMenuItem, disable: boolean): ListMenuItem => {
    if (item.type === 'MenuItemGrouped') {
      return {
        ...item,
        list: item.list.map(subItem => applyCustomOptionDisabling(subItem, disable)),
      };
    }

    if (item.type === 'MenuItem' && Array.isArray(item.value)) {
      return {
        ...item,
        value: item.value.map(subItem => applyCustomOptionDisabling(subItem, disable)),
      };
    }

    if (item.type !== 'MenuItem') {
      return item;
    }

    let shouldDisable = disable || item.isDisabled;

    const hasStandardFeatureConflict =
      !shouldDisable && !item.isCustom && typeof item.value === 'string' && shouldDisableStandardFeature(item.value);

    if (hasStandardFeatureConflict) {
      shouldDisable = true;
    }

    const hasSavedFeatureConflict =
      !shouldDisable &&
      item.label !== 'Custom' &&
      applicableSavedFeatureSegments.find(savedFeature => savedFeature.name === item.label);

    if (hasSavedFeatureConflict) {
      shouldDisable = shouldDisableCustomFeature(hasSavedFeatureConflict);
    }

    return {
      ...item,
      isDisabled: shouldDisable,
    };
  };

  useEffect(() => {
    if (!isOpen) return;

    menuItems.forEach(item => {
      if (item.type !== 'MenuItemContext') return;
      const { label, isDisabled, contextSetting } = item;
      const optionElement = document.querySelector(`[id="submenu-1 ${label}"]`);
      if (optionElement) {
        const targetDiv = optionElement.querySelector('.first-first-line');
        const editButton = targetDiv.querySelector('button');

        //need to remove existing button to make sure disabled state always reflect the current context menu item
        if (editButton) {
          editButton.closest('div').remove();
        }

        if (!editButton) {
          const labelDiv = optionElement.querySelector('div.list-label');
          const labelDivWidth = (labelDiv as HTMLDivElement).offsetWidth;
          const adjustedWidth = labelDivWidth - 40;
          (labelDiv as HTMLDivElement).style.width = adjustedWidth + 'px';
        }

        targetDiv.classList.add('items-center');
        const container = document.createElement('div');
        container.style.alignmentBaseline = 'central';
        targetDiv.appendChild(container);
        const root = createRoot(container);
        root.render(MenuEditButton({ isDisabled, contextSetting }));
      }
    });
  }, [isOpen]);

  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const { isMobile } = useMobile();

  useEffect(() => {
    if (!isOpen) {
      setWindowWidth(window.innerWidth);
    }
    if (isOpen && dropdownRef.current) {
      const rect = document.querySelector('.lists.show').getBoundingClientRect();

      if (isMobile && (rect?.right > windowWidth || rect?.left < 0 || rect?.left.toString() === 'auto')) {
        setIsOverflow(true);
      }
    }
  }, [isOpen, menuItems]);

  const handleOnClickButton = () => {
    setIsOpen(!isOpen);
  };
  const handleOnChangeMenuItem = (item: MenuItemOption, selectedValuePath: InitialSelectedListItem) => {
    //list box will pass null for item and undefined for selectedValuePath if an already selected option get selected
    if (!item) {
      item = selectedMenuOption;
    }
    if (!selectedValuePath) {
      selectedValuePath = selectedPath;
    }
    const hasChanged = item.value !== values[paramName]?.join('');
    if (!hasChanged && !item?.isCustom) {
      return;
    }
    if (item.label === 'View all') {
      handleViewAllClick(
        item,
        { setCustomizationTray, setIsFilterTray, setCustomizationTrayTabId },
        selectedCustomFeature,
        paramName,
        secondSegmentationConfig,
        page
      );
      return;
    }
    const newValues: Params = { ...{}, ...values };
    if (isSubMenuIndented && Array.isArray(item.value)) {
      /*
      This condition is true when the item is a brand owner group,
      hence we will need to use the label, bcs the value is an array of subbrands
      */
      newValues[paramName] = [`${BRAND_OWNER_GROUP_PREFIX}${item.label}`];
    } else {
      newValues[paramName] = [`${item.value}${appendBrandOwnerLabel(item, isSubMenuIndented)}`];
    }

    if (!['customTs', 'custom'].includes(item.value)) {
      setSelectedMenuOption(item);
      setSelectedPath(selectedValuePath);
    }

    if (item.isCustom) {
      handleCustomItemSelection(item, newValues);
    } else {
      handleStandardItemSelection(item, newValues);
    }
  };

  const handleStandardItemSelection = (item: ListBoxMenuItem, newValues: Params) => {
    if (customizationTraySettings && trayType) {
      setCustomizationTray(null);
      ContextManager.getInstance().setActiveCustomFeatureMenu(null);
    }
    handleSelectColumnItem(dataColumns, item, newValues);

    if (newValues.customFeatureTiers) {
      const filteredTiers = newValues.customFeatureTiers.filter(tier => !tier.startsWith(`${paramName}:`));
      newValues.customFeatureTiers = filteredTiers;
    }

    handleUpdateSettings(newValues, item.label);

    ContextManager.getInstance().setActiveCustomFeatureMenu(null);
    ContextManager.getInstance().updateCustomFeatureValues({
      customFeature: {
        [page.name]: {
          [paramName]: {
            selectedFeature: undefined,
            originMenu: undefined,
            customFeatureType: undefined,
            secondSegmentationConfig: undefined,
          },
        },
      },
    });
  };

  const handleUpdateSettings = (updates: Params, itemLabel: string) => {
    updates.item_label = [itemLabel];
    onUpdate(updates);
    trackPageEvent(EventName.FilterApplied, {
      filterName: menuConfig.metadata?.tracking?.filterName ?? paramName,
      filterType: TrackingElementType.Selectors,
      optionChosen: itemLabel,
    });
  };

  const handleCustomItemSelection = (item: ListBoxMenuItem, newValues: Params) => {
    setMenuCustomItem(item);

    if (item.label === 'Custom') {
      ContextManager.getInstance().setActiveCustomFeatureMenu(paramName);
      configureCustomMenu(item, newValues);
      return;
    }

    //if one of the saved custom price class is selected.
    if (checkIfCustomPriceTier(item)) {
      const selectedCustomPriceClass = savedPriceClasses.find(savedClass => savedClass.name === item.label);
      if (selectedCustomPriceClass) {
        newValues.customPriceTiers = [formatPricesForParams(selectedCustomPriceClass.priceClasses)];
        updateWithCustomClass(selectedCustomPriceClass.preferenceId, setIsActive, setCustomizationTray);
        handleUpdateSettings(newValues, item.label);
        ContextManager.getInstance().setActiveCustomFeatureMenu(null);
      }
      return;
    }

    const selectedCustomFeatureClass = applicableSavedFeatureSegments.find(
      savedClass => savedClass.name === item.label
    );

    if (selectedCustomFeatureClass) {
      const input: FormatFeatureClassInput = {
        selectedFeatureName: selectedCustomFeatureClass.segmentationName,
        selectedFeatureType: selectedCustomFeatureClass.filterType,
        segmentClasses: selectedCustomFeatureClass.customSegmentation,
        excludeOthers: false,
      };
      const formattedSavedCustomSeg = formatFeatureClassForParams(input);
      newValues.customFeatureTiers = [
        ...(newValues.customFeatureTiers || []).filter(tier => !tier.startsWith(`${paramName}:`)),
        `${paramName}:${formattedSavedCustomSeg}`,
      ];
      updateWithCustomClass(selectedCustomFeatureClass.preferenceId, setIsActive, setCustomizationTray);
      handleUpdateSettings(newValues, item.label);

      const validParamName = paramName && paramName !== 'undefined' ? paramName : null;

      if (validParamName) {
        ContextManager.getInstance().updateCustomFeatureValues({
          customFeature: {
            [page.name]: {
              [validParamName]: {
                selectedFeature: {
                  type: selectedCustomFeatureClass.filterType,
                  label: selectedCustomFeatureClass.segmentationLabel,
                  value: selectedCustomFeatureClass.segmentationName,
                  savedName: selectedCustomFeatureClass.name,
                },
                originMenu: validParamName,
                customFeatureType: `${SEGMENT_CUSTOM_FEATURE}|${FEATURE_SEGMENT}|${selectedCustomFeatureClass.name}`,
                secondSegmentationConfig: secondSegmentationConfig,
              },
            },
          },
        });
      }

      ContextManager.getInstance().setActiveCustomFeatureMenu(null);
    }
  };

  const configureCustomMenu = (item: ListBoxMenuItem, newValues: Params) => {
    const isFeatureCustomMenuItem = (item?.value as string)?.includes(SEGMENT_CUSTOM_FEATURE) ?? false;
    setIsFilterTray(false);
    setCustomizationTrayTabId(FilterTrayTabId.Configure);

    if (isFeatureCustomMenuItem) {
      setCustomizationTray(FEATURE_CUSTOMIZATION);

      ContextManager.getInstance().updateCustomFeatureValues({
        customFeature: {
          [page.name]: {
            [paramName]: {
              selectedFeature: selectedCustomFeature,
              originMenu: paramName,
              customFeatureType: item.value as string,
              secondSegmentationConfig: secondSegmentationConfig,
            },
          },
        },
      });

      // Only set initial feature class if no valid feature is selected for this menu
      const menuContext = pageCustomFeatures?.[paramName];

      if (!menuContext?.selectedFeature || !checkIsApplicableSavedFeature(baseFeatures, menuContext.selectedFeature)) {
        newValues.customFeatureTiers = [getInitialFeatureClass(sections, baseFeatures)];
      }
    } else {
      setCustomizationTray(PRICE_CUSTOMIZATION);

      handleUpdateSettings(newValues, item.label);
    }
  };

  const currentValue = values[paramName]?.[0] || defaultValue;

  const updateCustomComparisonLabel = (item: ListMenuItem): void => {
    if (item.type === 'MenuItem' && item.label.includes('custom comparison') && customPeriodLabel) {
      item.label = `custom comparison (${customPeriodLabel})`;
    }
  };

  const updateDefaultCustomTimeSeriesLabel = (item: ListMenuItem): void => {
    if (item.type !== 'MenuItemGrouped') {
      return;
    }

    item.list.forEach(item => updateCustomTimeSeriesLabel(item));
  };

  const updateCustomTimeSeriesLabel = (item: ListMenuItem): void => {
    const isTimeSeriesDuration = customMenuModalSettings?.customTimeSeriesDuration;

    if (!isTimeSeriesDuration || item.type !== 'MenuItem' || !item.isCustom) {
      return;
    }

    const isDefaultDisplayLabel = item.label.includes(
      customMenuModalSettings?.customTimeSeriesDuration?.defaultDisplayLabel
    );
    const isCurrentValueCustom = values[paramName]?.[0]?.includes('custom');

    if (isDefaultDisplayLabel && customTimeSeriesLabel) {
      item.label = customTimeSeriesLabel;
      return;
    }

    if (!isCurrentValueCustom) {
      item.label = customMenuModalSettings?.customTimeSeriesDuration.defaultDisplayLabel;
      setCustomTimeSeriesLabel(null);
    }
  };

  const findLabel = (items: ListMenuItem[], subMenuParentLabel?: string): string | undefined => {
    for (const item of items) {
      const label = handleItem(item, subMenuParentLabel);
      if (label) return label;
    }

    return undefined;
  };

  const handleItem = (item: ListMenuItem, subMenuParentLabel?: string): string | undefined => {
    if (item.type === 'MenuItem') {
      updateCustomComparisonLabel(item);
      updateCustomTimeSeriesLabel(item);
      return handleMenuItem(item as MenuItemOption, subMenuParentLabel);
    }

    if (item.type === 'MenuItemGrouped') {
      return findLabel(item.list, subMenuParentLabel);
    }

    if (item.type === 'MenuItemContext' && item.value === currentValue) {
      updateCustomComparisonLabel(item);
      const normalizedItem = { ...item, type: 'MenuItem' } as MenuItemOption;
      setSelectedPath({ label: item.label, value: item.value });
      setSelectedMenuOption(normalizedItem);
      return generateCustomLabel(normalizedItem, selectedCustomFeature);
    }

    return undefined;
  };

  const handleMenuItem = (item: MenuItemOption, subMenuParentLabel?: string): string | undefined => {
    const selectedItemLabel = currentValue?.includes(BRAND_OWNER_DELIMITER)
      ? currentValue.split(BRAND_OWNER_DELIMITER)[0]
      : currentValue;
    if (
      typeof item.value === 'string' &&
      (item.value === selectedItemLabel || (paramName === 'moHeatmapFocus' && item.value.startsWith(selectedItemLabel)))
    ) {
      setSelectedMenuOption(item);
      if (subMenuParentLabel && !isSubMenuIndented) {
        //right now we only have one level of submenu, so this should work but if we were two add more levels, some recurring function should replace this
        setSelectedPath({ label: subMenuParentLabel, value: { label: item.label, value: item.value } });
      } else {
        setSelectedPath({ label: item.label, value: item.value });
      }
      return generateCustomLabel(item, selectedCustomFeature);
    }

    //Item selected is a brand owner
    if (selectedItemLabel && selectedItemLabel === item.brandOwner) {
      const brandOwnerItem = {
        value: selectedItemLabel,
        label: selectedItemLabel,
        brandOwner: selectedItemLabel,
      } as MenuItemOption;
      setSelectedPath({ value: selectedItemLabel, label: selectedItemLabel });
      setSelectedMenuOption(brandOwnerItem);
      return selectedItemLabel;
    }

    if (Array.isArray(item.value)) {
      return findLabel(item.value, item.label);
    }

    return undefined;
  };

  const getLabel = (items: ListMenuItem[]): string => {
    if (items[0]?.type === 'MenuItemGrouped') {
      return findInGroupedItems(items as GroupedListBoxMenuItem[]);
    }

    return findLabel(items as ListBoxMenuItem[]) || '';
  };

  const findInGroupedItems = (groupedItems: GroupedListBoxMenuItem[]): string => {
    for (const group of groupedItems) {
      const label = findLabel(group.list);
      if (label) return label;
    }
    return '';
  };

  const displayLabel = useMemo(() => {
    if (buttonLabel) return buttonLabel;

    const currentValue = values[paramName]?.[0];

    if (currentValue?.includes(`${SEGMENT_CUSTOM_FEATURE}|${FEATURE_SEGMENT}`)) {
      const currentFeatureName = currentValue.split('|')[2];

      if (selectedCustomFeature?.savedName) {
        return `${selectedCustomFeature.savedName} (Custom)`;
      }

      if (selectedCustomFeature?.label) {
        return `${selectedCustomFeature.label} (Custom)`;
      }

      if (currentFeatureName && currentFeatureName !== 'View all') {
        return `${currentFeatureName} (Custom)`;
      }
    }

    const label = getLabel(menuItems) || '...';
    return formatSharedBrandsTotal(label);
  }, [buttonLabel, values, paramName, menuItems, selectedCustomFeature?.label, customPeriodLabel]);

  const secondSegmentationConfig: SecondFeatureSegmentationConfig = {
    valuesToPrevent: valuesToPrevent,
    preventDuplicatesWith: preventDuplicatesWith,
  };

  const handleApply = (start: Period[], end: Period[]) => {
    const periodLabel = start
      .map((s, i) => {
        const e = end[i];
        return s.periodSeq === e.periodSeq ? s.name : `${s.name} - ${e.name}`;
      })
      .join(', ');
    setCustomPeriodLabel(periodLabel);
    const newValues: Params = { ...{}, ...values };
    const customSeq = [];
    start.forEach((_, index) => {
      customSeq.push(`${start[index].periodSeq}-${end[index].periodSeq}`);
    });
    newValues[paramName] = [menuCustomItem.value, ...customSeq];
    handleUpdateSettings(newValues, menuCustomItem.label);
    setMenuCustomItem(null);
  };

  const handleCustomSeriesApply = (value: string) => {
    const newValues: Params = { ...{}, ...values };
    newValues[paramName] = [menuCustomItem.value as string, value];

    setCustomTimeSeriesLabel(menuCustomItem.label);

    handleUpdateSettings(newValues, menuCustomItem.label);
    setMenuCustomItem(null);
  };

  const handleCustomModalClose = () => {
    // since we are conditionally rendering the DS modal component, we need to reset the overflow when closing the modal
    document.body.style.overflow = 'unset';
    setMenuCustomItem(null);
  };

  const getSelectableItemCount = (items: ListMenuItem[]): number => {
    let count = 0;
    items.forEach(item => {
      if (item.type === 'MenuItem') {
        count++;
      } else if (item.type === 'MenuItemGrouped') {
        count += item.list.length;
      } else if (item.type === 'MenuItemContext') {
        count++;
      }
    });
    return count;
  };

  const isCustomComparisonModalOpen = !!menuCustomItem && !!customMenuModalSettings?.customComparisonPeriods;
  const isCustomTimeSeriesDurationModalOpen =
    !!menuCustomItem && !!customMenuModalSettings && !!customMenuModalSettings?.customTimeSeriesDuration;

  const ButtonElement = button;

  if (inactive || (menuConfig.disableOnSingleItem && getSelectableItemCount(menuItems) === 1)) {
    return <div className="text-brand">{displayLabel}</div>;
  }

  if (menuItems.length === 1 && menuItems[0].type === 'MenuItemContext') {
    return <MenuContextButton isLoading={isLoading} item={menuItems[0]} inactive={inactive} />;
  }

  const isButtonDisabled = (!menuItems.length && !defaultValue) || isLoading;

  const flattenedMenuItems = flattenMenuItems(menuItems);

  const showCombobox = flattenedMenuItems.length >= 10;

  if (!flattenMenuItems.length) {
    return;
  }

  const renderButtonElement = () => {
    const button = (
      <ButtonElement disabled={isButtonDisabled} onClick={handleOnClickButton}>
        {displayLabel}
      </ButtonElement>
    );
    //need this flag because listbox cannot size properly when theres relative (indicator) or sticky class (page heading)
    if (useListBoxButtonSlot) {
      return <ListBox.ButtonSlot>{button}</ListBox.ButtonSlot>;
    }
    return button;
  };

  const displayUpsellFooter: boolean = data?.getUpsellKpisIndicator() && paramName === 'kpi';

  return (
    <>
      <div
        className={classNames('flex items-center text-base', {
          'mobile-list-overflow': isMobile && isOverflow,
        })}
        ref={dropdownRef}
      >
        <ListBox
          key={`${paramName}-${shouldDisableCustom ? 'disabled' : 'enabled'}`}
          list={menuItems as any}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          sectionDivider="thin"
          width={300}
          onChange={(item, selectedValuePath) => handleOnChangeMenuItem(item as MenuItemOption, selectedValuePath)}
          value={selectedPath}
          showGroupLabel={showGroupLabel}
          combobox={showCombobox}
          isSubMenuIndented={isSubMenuIndented}
          hasFooter={displayUpsellFooter}
          footerContent={<UpsellFooter footerType={'kpi'} displayUpsellFooter={true} />}
        >
          {renderButtonElement()}
        </ListBox>
      </div>

      {isCustomComparisonModalOpen && (
        <CustomComparisonModal
          isOpen={!!menuCustomItem}
          onClose={handleCustomModalClose}
          onApply={handleApply}
          currentSelectedPeriods={values[paramName]}
          customMenuModalSettings={customMenuModalSettings}
        />
      )}

      {isCustomTimeSeriesDurationModalOpen && (
        <CustomTimeSeriesModal
          menuItemOption={menuCustomItem as MenuItemOption}
          setMenuCustomItem={setMenuCustomItem}
          isOpen={!!menuCustomItem}
          onClose={handleCustomModalClose}
          onApply={handleCustomSeriesApply}
          currentSelectedPeriods={values[paramName]}
          customMenuModalSettings={customMenuModalSettings}
        />
      )}
    </>
  );
}

export { SelectionMenuComponent };
